/**
 * useStaticMenuQuery
 *
 * Adapts BeHeard's static menu (menuConfig.ts) to the shell's
 * `useGetMenuQuery` contract by evaluating each item's visibility
 * against the EFFECTIVE workspace context — including Testing Mode
 * overrides injected by BootGate.
 *
 * Visibility rules:
 *
 *   Admin entry    → only when GET /capabilities lists an admin function
 *                    for the caller (any role; never from a role label).
 *   owner / admin  → ALL other items visible (full access).
 *   viewer         → only items with `viewerVisible: true`.
 *   member         → items where:
 *     a) no `requireScope` (always visible to any member), OR
 *     b) `requireScope` is present in `workspace.scopes`.
 *
 * Scopes come from `workspace.scopes` (Redux boot context), which
 * BootGate populates from Central's switch-context response AND
 * optionally from the Testing Mode sessionStorage overrides
 * (`auth/simulatedAppProfile.ts` → SCOPE_BUNDLES). So the menu
 * reacts correctly to both real and simulated access.
 *
 * No local role matrix. No fake authorization. Backend remains the
 * authority on every API call.
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { STATIC_MENU, adminEntryAllows, menuServerSwitches, serverSwitchAllows } from '../menuConfig';
import { useGetCapabilitiesQuery } from '../../store/api/capabilitiesApi';
import { selectBootCurrentWorkspace } from '../../store/slices/bootContextSlice';
import { useCentralConsumer } from '../../hooks/useCentralConsumer';

interface ShellMenuItem {
  menuKey: string;
  label: string;
  order: number;
  isVisible: boolean;
  permissions: { canView: boolean };
}

export function useStaticMenuQuery(
  _workspaceId: string,
  _options?: { skip?: boolean },
) {
  const { hasWorkspaceRole, bootCompleted } = useCentralConsumer();
  const workspace = useSelector(selectBootCurrentWorkspace);

  const effectiveRole = (workspace?.role ?? 'member') as string;
  const effectiveScopes = workspace?.scopes ?? [];
  const { data: capsDoc } = useGetCapabilitiesQuery(undefined, { skip: !bootCompleted });
  const serverCaps = useMemo(() => menuServerSwitches(capsDoc), [capsDoc]);

  const data = useMemo(() => {
    if (!bootCompleted) {
      // During boot show only ungated items to avoid flash
      const menu: ShellMenuItem[] = STATIC_MENU
        .filter((item) => !item.requireScope && (item.viewerVisible ?? false))
        .map((item) => ({
          menuKey: item.menuKey,
          label: item.label,
          order: item.order,
          isVisible: true,
          permissions: { canView: true },
        }));
      return { menu };
    }

    // Owner / admin — full access, bypass all gates
    const isFullAccess = hasWorkspaceRole('owner', 'admin');

    const scopeSet = new Set(effectiveScopes);

    const menu: ShellMenuItem[] = STATIC_MENU.filter((item) => {
      // Server feature switches apply to every role
      if (!serverSwitchAllows(item, serverCaps)) return false;
      // The Admin entry follows the server's admin functions, for every role
      if (!adminEntryAllows(item, serverCaps)) return false;
      if (item.requireAdminFunction) return true;

      // Owner / admin see everything
      if (isFullAccess) return true;

      // Viewer — only items explicitly marked viewerVisible
      if (effectiveRole === 'viewer') {
        return item.viewerVisible === true;
      }

      // Member (or any other non-owner/admin/viewer role)
      // Items without a scope gate are visible to all members
      if (!item.requireScope) return true;

      // Items with a scope gate require the scope in the effective set
      return scopeSet.has(item.requireScope);
    }).map((item) => ({
      menuKey: item.menuKey,
      label: item.label,
      order: item.order,
      isVisible: true,
      permissions: { canView: true },
    }));

    return { menu };
  }, [bootCompleted, hasWorkspaceRole, effectiveRole, effectiveScopes, serverCaps]);

  return {
    data,
    isLoading: false,
    error: undefined,
  };
}
